import { useMutation } from '@tanstack/react-query';
import nanoid from 'nanoid';

import container from '../../../../container';
import { currencies } from '../claimsHelpers';
import { useClaimsShopSettings } from '../../ClaimsShopSettingsContext';
import { ClaimContext } from '../types';

type OnChangeFn = (payload: Record<string, unknown>) => void;

export type InvoiceSource = 'manual' | 'import';

const useInvoiceExtraction = (
  claim: Pick<ClaimContext, 'orderItems'>,
  onChange: OnChangeFn,
  isOrderItemTaxRate: boolean,
  isCashOnDelivery: boolean
) => {
  const shopSettings = useClaimsShopSettings();

  const { isLoading, mutate, isError } = useMutation({
    mutationFn: async (fileId: number) => container.touchpointsApi.getInvoiceExtraction(fileId),
    onSuccess: (data) => {
      const { netValue, netValueCurrency, grossValue, orderItems, taxRate } = data;

      if (netValueCurrency && currencies.includes(netValueCurrency)) {
        onChange({ netValueCurrency });
      }

      let isTaxIncluded: boolean = false;
      if (isOrderItemTaxRate) {
        isTaxIncluded = orderItems && orderItems.some((item) => item.taxRate);
      } else {
        isTaxIncluded = !!taxRate;
      }

      onChange({
        netValue: isCashOnDelivery ? grossValue : netValue,
        taxRate: taxRate ? taxRate * 100 : null,
        orderItems: orderItems.map((item) => ({
          id: nanoid(),
          description: item.name ? item.name.replace(/\s+/g, ' ').trim().substring(0, 50).split(/,|;/)[0] : '',
          unitPrice: String(item.price || 0),
          quantity: String(Math.floor(item.quantity || 0)),
          taxRate: item.taxRate ? item.taxRate * 100 : null,
        })),
        isTaxIncluded,
      });
    },
  });

  const extractInvoiceData = (fileId: number, invoiceSource?: InvoiceSource) => {
    const shouldExtractOrderItems =
      shopSettings.isInvoiceExtractionEnabled && !(claim.orderItems.length > 0 && invoiceSource === 'import');

    if (shouldExtractOrderItems) {
      mutate(fileId);
    }
  };

  return {
    isExtractingInvoice: isLoading,
    extractInvoiceData,
    isFailedToExtractInvoice: isError,
  };
};

export default useInvoiceExtraction;
